import { embedParentOriginMatchesMessage } from '../embed_parent_origin'
import { getVendorStatuses } from '../vendors/vendor_runtime'

/**
 * Iframe → parent embed consent state messaging.
 *
 * The embedded booking page already maintains a resolved `ConsentState`
 * via `consent_module.js`. This transport exposes that state to the embedding
 * host so it can observe the effective consent the iframe is operating with
 * (after the host CMP + Calendly OneTrust precedence has been applied).
 *
 * Wire contract
 * -------------
 * Parent → iframe (request a snapshot, and subscribe to subsequent changes):
 *
 *   {
 *     type: 'calendly.consent.state.get.v1',
 *   }
 *
 * Iframe → parent (snapshot/notification):
 *
 *   {
 *     type: 'calendly.consent.state.v1',
 *     state: {
 *       // Resolved (always 'granted' | 'denied' per category)
 *       categories: { necessary, functional, performance, targeting },
 *       source: 'host' | 'onetrust' | 'default',
 *       // Raw inputs that produced the resolution (informational; shape may
 *       // evolve — consumers should depend on `categories`/`source`).
 *       host: null | { source, categories, framework?, updatedAt? },
 *       onetrust: null | { categories },
 *       vendors: VendorRuntimeStatus[],
 *     },
 *   }
 *
 * Security model: identical to the inbound `calendly.consent.update.v1`
 * listener. We only respond to a request when the request's origin host matches
 * the `embed_domain` query parameter on the iframe URL (which `widget.js` sets
 * from the embedding host). Subsequent change notifications are sent only to
 * that verified origin via `window.parent.postMessage(..., verifiedOrigin)`.
 *
 * Subscription is implicit: sending a `get.v1` request both fetches the current
 * snapshot AND opts the parent in to future change notifications until the
 * iframe is torn down. Sending another `get.v1` from the same parent is a
 * no-op for subscription but always returns a fresh snapshot.
 */

const STATE_REQUEST_MESSAGE_EVENT = 'calendly.consent.state.get.v1'
const STATE_MESSAGE_EVENT = 'calendly.consent.state.v1'

let verifiedParentOrigin = null

/** @type {import('../vendors/vendor_runtime').VendorRuntimeContext} */
let vendorRuntimeContext = {}

/**
 * Update runtime inputs used when building the `vendors` array on state payloads.
 * Called from React components that own feature-flag state (e.g. ConsentStatePublisher).
 *
 * @param {import('../vendors/vendor_runtime').VendorRuntimeContext} context
 */
export const setVendorRuntimeContext = (context) => {
  vendorRuntimeContext = context ?? {}
}

const buildPayload = (state) => ({
  type: STATE_MESSAGE_EVENT,
  state: {
    categories: state.categories,
    source: state.source,
    host: state.host,
    onetrust: state.onetrust,
    vendors: getVendorStatuses(state.categories, vendorRuntimeContext),
  },
})

const canPostToParent = () => (
  typeof window !== 'undefined' && window.parent && window.parent !== window
)

const postToParent = (payload, targetOrigin) => {
  if (!canPostToParent()) return
  try {
    window.parent.postMessage(payload, targetOrigin)
  } catch {
    /* noop — a broken parent must not break the iframe */
  }
}

/**
 * Handle a parent → iframe state request. Verifies origin, records the parent
 * as a subscriber, and replies with the current resolved state.
 *
 * @param {MessageEvent} event
 * @param {() => object} getState
 */
export const handleConsentStateRequest = (event, getState) => {
  if (!event?.data || event.data.type !== STATE_REQUEST_MESSAGE_EVENT) return
  if (!embedParentOriginMatchesMessage(window.location.search, event.origin)) return

  verifiedParentOrigin = event.origin
  postToParent(buildPayload(getState()), verifiedParentOrigin)
}

/**
 * Publish a resolved state change to the verified parent. No-op when no parent
 * has subscribed (no `get.v1` has been received yet), so unsolicited state is
 * never broadcast to `*`.
 *
 * @param {object} state Resolved ConsentState from consentModule.getState()
 */
export const publishConsentState = (state) => {
  if (!verifiedParentOrigin) return
  postToParent(buildPayload(state), verifiedParentOrigin)
}

/** Test-only: clear the recorded subscriber between tests. */
export const resetConsentStatePublisherForTests = () => {
  verifiedParentOrigin = null
  vendorRuntimeContext = {}
}
